import classNames from "classnames";
import React, { FC, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { ContactFileModel } from "../../factories/contacts/models/contact-file.model";
import {
  useGetClientPortalQuery,
  useShareWithClientMutation,
  useUploadAndShareMutation,
} from "../../slices/ClientPortalSlice";
import { FileBadge } from "../portal/components/PortalUi";
import { fileProblem, formatDate, PORTAL_ACCEPT } from "../portal/portal.utils";
import { personName, portalFileState, possessive } from "./clientPortal.utils";
import {
  errorText,
  modalCancel,
  modalPrimary,
  PortalModal,
} from "./PortalModal";

type Tab = "files" | "upload";

/** Put files in the client's portal: pick existing client files or upload new ones. */
export const ShareWithClientModal: FC<{
  customerId: string;
  clientName?: string;
  files: ContactFileModel[];
  onClose: () => void;
  onDone: () => void;
}> = ({ customerId, clientName: givenName, files, onClose, onDone }) => {
  const { data: portal } = useGetClientPortalQuery({ customerId });
  const clientName = givenName || portal?.clientName || "this client";
  const [tab, setTab] = useState<Tab>("files");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [share, shareState] = useShareWithClientMutation();
  const [upload, uploadState] = useUploadAndShareMutation();
  const input = useRef<HTMLInputElement>(null);

  // Firm files that aren't in the portal yet (the client's own uploads are always there).
  const candidates = useMemo(
    () => files.filter((f) => portalFileState(f) === "firm"),
    [files],
  );
  const active = (portal?.people ?? []).filter((p) => p.status === "active");
  const count = tab === "files" ? picked.size : newFiles.length;
  const busy = shareState.isLoading || uploadState.isLoading;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const chosen = Array.from(list);
    const problems = chosen.map(fileProblem).filter(Boolean);
    setError(problems.length ? problems.join(" ") : null);
    setNewFiles((f) => [...f, ...chosen.filter((x) => !fileProblem(x))]);
  };

  const submit = async () => {
    setError(null);
    const doNotify = notify && active.length > 0;
    const result =
      tab === "files"
        ? await share({
            documentIds: Array.from(picked),
            shared: true,
            notify: doNotify,
            message: message.trim() || undefined,
          })
        : await upload({
            customerId,
            files: newFiles,
            notify: doNotify,
            message: message.trim() || undefined,
          });
    if ("error" in result) return setError(errorText(result.error));
    const { changed, emailed } = result.data;
    toast(
      `${
        changed === 1 ? "1 file" : `${changed} files`
      } shared with ${clientName}${emailed ? ` · ${emailed} emailed` : ""}`,
      { type: "success" },
    );
    onDone();
    onClose();
  };

  const tabClass = (on: boolean) =>
    classNames(
      "h-9 rounded-lg px-3.5 text-[13px]",
      on
        ? "bg-[#F0EDFF] font-extrabold text-[#4B2FC7]"
        : "border-[1.5px] border-[#DCE2EA] font-bold text-[#3A4256]",
    );
  const names = active.map(personName);

  return (
    <PortalModal
      title={"Share with client"}
      subtitle={`Shows up in ${possessive(clientName)} portal`}
      onClose={onClose}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            onClick={submit}
            disabled={busy || count === 0}>
            {busy
              ? "Sharing…"
              : count === 1
              ? "Share 1 file"
              : `Share ${count} files`}
          </button>
        </>
      }>
      <div className={"flex gap-2"}>
        <button
          type={"button"}
          className={tabClass(tab === "files")}
          onClick={() => setTab("files")}>
          Client’s files
        </button>
        <button
          type={"button"}
          className={tabClass(tab === "upload")}
          onClick={() => setTab("upload")}>
          Upload new
        </button>
      </div>

      {tab === "files" ? (
        candidates.length ? (
          <div
            className={
              "flex max-h-[280px] flex-col overflow-y-auto rounded-[10px] border border-[#EDEFF3]"
            }>
            {candidates.map((f) => {
              const name = f.originalFileName || f.documentName || "File";
              const on = picked.has(f.id);
              return (
                <label
                  key={f.id}
                  className={classNames(
                    "flex cursor-pointer items-center gap-3 border-b border-[#F4F5F7] px-3.5 py-2.5 last:border-b-0",
                    on && "bg-[#FAF9FF]",
                  )}>
                  <input
                    type={"checkbox"}
                    className={"h-4 w-4 accent-[#6B4EFF]"}
                    checked={on}
                    onChange={() =>
                      setPicked((s) => {
                        const n = new Set(s);
                        if (n.has(f.id)) n.delete(f.id);
                        else n.add(f.id);
                        return n;
                      })
                    }
                  />
                  <FileBadge name={name} />
                  <span className={"flex min-w-0 flex-1 flex-col"}>
                    <span className={"truncate text-[13.5px] font-bold"}>
                      {name}
                    </span>
                    <span className={"text-xs font-medium text-[#6B7185]"}>
                      {formatDate(f.createdDate)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <span className={"text-[13px] font-medium text-[#6B7185]"}>
            All of this client’s files are already in the portal. Use “Upload
            new” to add more.
          </span>
        )
      ) : (
        <div className={"flex flex-col gap-2"}>
          <form className={"hidden"}>
            <input
              ref={input}
              type={"file"}
              multiple={true}
              accept={PORTAL_ACCEPT}
              onChange={(e) => {
                addFiles(e.target.files);
                e.currentTarget.form?.reset();
              }}
            />
          </form>
          <button
            type={"button"}
            onClick={() => input.current?.click()}
            className={
              "flex h-[88px] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#C9BFFB] bg-[#FAF9FF] text-[13.5px] font-extrabold text-[#4B2FC7]"
            }>
            Choose files
            <span className={"text-xs font-medium text-[#6B7185]"}>
              PDF, photos, Word or Excel · up to 50 MB each
            </span>
          </button>
          {newFiles.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className={
                "flex items-center gap-3 rounded-[10px] border border-[#EDEFF3] px-3.5 py-2.5"
              }>
              <FileBadge name={f.name} />
              <span
                className={"min-w-0 flex-1 truncate text-[13.5px] font-bold"}>
                {f.name}
              </span>
              <button
                type={"button"}
                aria-label={`Remove ${f.name}`}
                className={"text-[#9097A8]"}
                onClick={() => setNewFiles((x) => x.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={"flex flex-col gap-1.5"}>
        <label
          htmlFor={"portal-share-message"}
          className={"text-[13px] font-bold text-[#3A4256]"}>
          Message (optional)
        </label>
        <textarea
          id={"portal-share-message"}
          rows={2}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={"e.g. Here’s your Q3 financial statements."}
          className={
            "w-full resize-none rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3 py-2.5 text-[13.5px] outline-none placeholder:text-[#9097A8] focus:border-[#6B4EFF]"
          }
        />
      </div>
      {active.length ? (
        <label
          className={
            "flex cursor-pointer items-start gap-2.5 text-[13px] font-semibold text-[#3A4256]"
          }>
          <input
            type={"checkbox"}
            className={"mt-0.5 h-4 w-4 accent-[#6B4EFF]"}
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          Email{" "}
          {names.length <= 2
            ? names.join(" and ")
            : `${names.slice(0, 2).join(", ")} and ${
                names.length - 2
              } more`}{" "}
          that something new is in the portal
        </label>
      ) : (
        <span className={"text-[12.5px] font-medium text-[#6B7185]"}>
          Nobody has signed in to this client’s portal yet, so no email is sent.
          They’ll see the file when they sign in.
        </span>
      )}
      {error && (
        <div
          role={"alert"}
          className={
            "rounded-[9px] bg-[#FDECEC] px-3.5 py-2.5 text-[13px] font-semibold text-[#B42318]"
          }>
          {error}
        </div>
      )}
    </PortalModal>
  );
};
